import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Box, Tooltip, Typography } from "@mui/material";
import { Avatar, ConfirmDialog, IconButton } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Contact01Icon,
  Delete02Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { Inline, Stack } from "components/layout/reusableLayouts";
import CustomSkeleton from "components/common/CustomSkeleton";
import { COMMENT_FROM_CONTACT_USER_ID } from "components/common/Constants";
import UserProfileDrawer from "components/common/users/UserProfileDrawer";
import useGetLoggedInUser from "hooks/useGetLoggedInUser";
import { CommentActions } from "hooks/useComments";
import { fullName } from "utils/fullName";
import { renderCommentWithMentions } from "./mentions";
import UserAvatar from "components/common/users/UserAvatar";

interface CommentListProps {
  actions: CommentActions;
  /** Mentions open the admin user profile. Turn off where the viewer cannot use it. */
  openProfileOnMention?: boolean;
}

// Same reveal as AllocationTable: opacity keeps the button focusable, and
// transparent must also mean untappable.
const rowSx = {
  "& [data-row-actions]": { opacity: 0, pointerEvents: "none" },
  "&:hover [data-row-actions], &:focus-within [data-row-actions]": {
    opacity: 1,
    pointerEvents: "auto",
  },
  "@media (hover: none)": {
    "& [data-row-actions]": { opacity: 1, pointerEvents: "auto" },
  },
};

const CommentList: React.FC<CommentListProps> = ({
  actions,
  openProfileOnMention = true,
}) => {
  const { t } = useTranslation();
  const { user: viewer } = useGetLoggedInUser();
  const [profileUserId, setProfileUserId] = useState<string>();
  const [pendingDeleteId, setPendingDeleteId] = useState<string>();
  const endRef = useRef<HTMLDivElement>(null);
  const shownCount = useRef<number | null>(null);
  const { comments, isDraft, loadingComments, deleteComment, deletingComment } =
    actions;

  // A failure is already toasted, so the dialog stays open for a retry.
  const confirmDelete = async () => {
    if (pendingDeleteId && (await deleteComment(pendingDeleteId))) {
      setPendingDeleteId(undefined);
    }
  };

  // Only a comment that arrives after the first load scrolls, so opening a
  // drawer never jumps to its comments.
  useEffect(() => {
    if (!comments) {
      shownCount.current = null;
      return;
    }
    if (shownCount.current !== null && comments.length > shownCount.current) {
      endRef.current?.scrollIntoView?.({
        block: "nearest",
        behavior: "smooth",
      });
    }
    shownCount.current = comments.length;
  }, [comments]);

  if (isDraft) return null;
  if (loadingComments && !comments) {
    return <CustomSkeleton variant="comment" rows={2} />;
  }

  // The dialog sits outside the list so deleting the last comment, which
  // empties the list, does not unmount it mid-close.
  return (
    <>
      {comments?.length ? (
        <Box>
          <Stack gap="var(--space-7)">
            {comments.map((comment) => {
              const name = fullName(comment.user);
              return (
                <Inline
                  key={comment.id}
                  align="start"
                  gap="var(--space-3)"
                  wrap={false}
                  sx={rowSx}
                >
                  {comment.user.id === COMMENT_FROM_CONTACT_USER_ID ? (
                    <Avatar
                      size="sm"
                      color="brand-soft"
                      icon={<HugeiconsIcon icon={Contact01Icon} />}
                    />
                  ) : (
                    <UserAvatar size="sm" user={comment.user} />
                  )}
                  <Stack gap="var(--space-1)" sx={{ flex: 1, minWidth: 0 }}>
                    <Inline align="baseline" gap="var(--space-2)">
                      <Typography variant="label">{name}</Typography>
                      <Tooltip
                        title={moment(comment.commentDate).format(
                          "MMM D, YYYY • h:mm A",
                        )}
                        placement="top"
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "var(--color-text-tertiary)" }}
                        >
                          {moment(comment.commentDate).fromNow()}
                        </Typography>
                      </Tooltip>
                    </Inline>
                    <Typography
                      variant="body1"
                      component="div"
                      sx={{
                        color: "var(--color-text-secondary)",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {renderCommentWithMentions(
                        comment.comment,
                        comment.mentions ?? [],
                        openProfileOnMention ? setProfileUserId : undefined,
                      )}
                    </Typography>
                  </Stack>
                  {viewer?.id === comment.user.id && (
                    <Box data-row-actions>
                      <IconButton
                        aria-label={t("Delete comment")}
                        color="danger"
                        variant="soft"
                        size="small"
                        onClick={() => setPendingDeleteId(comment.id)}
                      >
                        <HugeiconsIcon icon={Delete02Icon} size={16} />
                      </IconButton>
                    </Box>
                  )}
                </Inline>
              );
            })}
          </Stack>
          <div ref={endRef} />
        </Box>
      ) : null}

      <ConfirmDialog
        open={!!pendingDeleteId}
        tone="danger"
        title={t("Delete comment")}
        message={t("Are you sure you want to delete this comment?")}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        busy={deletingComment}
        onConfirm={confirmDelete}
        onCancel={() => {
          if (!deletingComment) setPendingDeleteId(undefined);
        }}
        icon={
          <HugeiconsIcon icon={Delete02Icon} size={24} color="currentColor" />
        }
      />

      {openProfileOnMention && (
        <UserProfileDrawer
          selectedUserId={profileUserId || ""}
          open={!!profileUserId}
          handleCloseDrawer={() => setProfileUserId(undefined)}
        />
      )}
    </>
  );
};

export default CommentList;
